import { useForm } from "@inertiajs/react"
import type { FormEvent } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { listsPath } from "@/routes"

interface CreateListModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateListModal({ open, onOpenChange }: CreateListModalProps) {
  const form = useForm({
    list: {
      name: "",
      auto_archive_days: 7,
      auto_clean_days: 7,
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    form.post(listsPath(), {
      preserveScroll: true,
      onSuccess: () => {
        form.reset()
        onOpenChange(false)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>CREATE NEW LIST</DialogTitle>
            <DialogDescription>
              A new domain of tasks to conquer... or let die
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label
                htmlFor="new-list"
                className="font-mono text-xs tracking-wider uppercase"
              >
                List Name
              </Label>
              <Input
                id="new-list"
                value={form.data.list.name}
                onChange={(e) =>
                  form.setData("list", {
                    ...form.data.list,
                    name: e.target.value,
                  })
                }
                placeholder="e.g., Work Tasks, Personal Goals"
                autoComplete="off"
                autoFocus
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label
                  htmlFor="create-auto-archive-days"
                  className="font-mono text-xs tracking-wider uppercase"
                >
                  Time to Live
                </Label>
                <Select
                  value={form.data.list.auto_archive_days.toString()}
                  onValueChange={(value) =>
                    form.setData("list", {
                      ...form.data.list,
                      auto_archive_days: parseInt(value),
                    })
                  }
                >
                  <SelectTrigger
                    id="create-auto-archive-days"
                    className="w-full"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 3, 7, 14, 30].map((days) => (
                      <SelectItem key={days} value={days.toString()}>
                        {days} {days === 1 ? "day" : "days"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-bone-muted text-xs">
                  How long tasks stay alive
                </p>
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="create-auto-clean-days"
                  className="font-mono text-xs tracking-wider uppercase"
                >
                  Burial
                </Label>
                <Select
                  value={form.data.list.auto_clean_days.toString()}
                  onValueChange={(value) =>
                    form.setData("list", {
                      ...form.data.list,
                      auto_clean_days: parseInt(value),
                    })
                  }
                >
                  <SelectTrigger id="create-auto-clean-days" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[3, 7, 14, 30].map((days) => (
                      <SelectItem key={days} value={days.toString()}>
                        {days} {days === 1 ? "day" : "days"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-bone-muted text-xs">
                  When dead tasks are buried
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={form.processing}>
              Create List
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
